import { useT } from "../../../shared/i18n/i18n";
import { CornerLeftUp, Loader2, MoreHorizontal } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCurrentUser } from "../../access/model/use-current-user";
import { useWorkspaceMembers } from "../../access/model/use-workspace-members";
import { useWorkObjectDocument } from "../../document/model/use-document";
import { useInboundRelations } from "../model/use-inbound-relations";
import { DocumentEditor } from "../../document/ui/document-editor";
import type { WorkObjectFieldDefinition } from "../../work-object-field/model/use-work-object-fields";
import type { WorkObjectAuditEvent } from "../model/use-work-object-audit";
import { useWorkObjectComments, type WorkObjectComment } from "../model/use-work-object-comments";
import {
  buildFieldValueInput,
  createFieldValueDrafts,
  displayValueFieldDefinitions,
  fieldDefinitionsForType,
  isLegacyDescriptionField,
  type WorkObjectFieldValueDraft,
} from "../model/work-object-field-drafts";
import type { WorkObjectFieldValueItem } from "../model/use-work-object-field-values";
import type { UpdateWorkObjectInput, WorkObjectListItem, WorkObjectListView } from "../model/use-work-objects";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { CommentComposer } from "./comment-composer";
import { WorkObjectActivity } from "./work-object-activity";
import { WorkObjectPropertyList } from "./work-object-fields-form";

type SaveStatus = "error" | "idle" | "saved" | "saving";

type WorkObjectDetailDialogProps = {
  fieldDefinitions: WorkObjectFieldDefinition[];
  auditEvents: WorkObjectAuditEvent[];
  fieldValues: WorkObjectFieldValueItem[];
  isArchiving: boolean;
  isLoadingAudit: boolean;
  isLoadingFieldValues: boolean;
  isSaving: boolean;
  item: WorkObjectListItem | null;
  listView: WorkObjectListView;
  onArchive: (id: string) => Promise<void>;
  // Убрать поле у этого типа карточек. Само правило живёт в схеме поля —
  // карточка лишь даёт до него дотянуться там, где стало видно, что поле
  // здесь лишнее.
  onHideFieldForType?: (field: WorkObjectFieldDefinition, typeId: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onOpenRelated?: (workObjectId: string) => void;
  onSave: (input: UpdateWorkObjectInput, values: WorkObjectFieldValueItem[]) => Promise<WorkObjectListItem>;
  open: boolean;
  // Names for referenced ids the pickable lists don't cover, so related items
  // and former members still read as themselves instead of vanishing.
  referenceLabels?: Record<string, string>;
  // The board-context status control (status lives on the board item, not on
  // the work object), rendered next to the type selector.
  statusControl?: ReactNode;
};

export function WorkObjectDetailDialog({
  fieldDefinitions,
  auditEvents,
  fieldValues,
  isArchiving,
  isLoadingAudit,
  item,
  listView,
  onArchive,
  onHideFieldForType,
  onOpenChange,
  onOpenRelated,
  onSave,
  referenceLabels,
  open,
  statusControl,
}: WorkObjectDetailDialogProps) {
  const [title, setTitle] = useState("");
  const [typeId, setTypeId] = useState("");
  const [fieldValueDrafts, setFieldValueDrafts] = useState<Record<string, WorkObjectFieldValueDraft>>({});
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<WorkObjectComment | null>(null);

  const comments = useWorkObjectComments(open ? item?.id ?? null : null);
  const workObjectDocument = useWorkObjectDocument(open ? item?.id ?? null : null);
  const inboundRelations = useInboundRelations(open ? item?.id ?? null : null);
  const members = useWorkspaceMembers(open);
  const { currentUserId } = useCurrentUser();
  const t = useT();
  const mentionMembers = useMemo(
    () => members.data.map((member) => ({ id: member.userId, label: member.displayName ?? member.email })),
    [members.data],
  );

  const applicableFieldDefinitions = useMemo(() => fieldDefinitionsForType(fieldDefinitions, typeId), [fieldDefinitions, typeId]);
  const valueFieldDefinitions = useMemo(() => {
    const valuesByFieldId = new Map(fieldValues.map((value) => [value.fieldDefinitionId, value.value]));
    return displayValueFieldDefinitions(applicableFieldDefinitions).filter((field) => {
      if (!isLegacyDescriptionField(field)) return true;
      const value = valuesByFieldId.get(field.id);
      return Array.isArray(value) ? value.length > 0 : Boolean(value && value.trim());
    });
  }, [applicableFieldDefinitions, fieldValues]);
  const selectedType = listView.types.find((type) => type.id === typeId);
  // Путь вверх по ветке: от задачи к истории, от истории к эпику. Вниз ведёт
  // список входящих связей ниже — вместе они дают проход по всей ветке.
  const parent = onOpenRelated ? hierarchyParent(valueFieldDefinitions, fieldValueDrafts) : null;
  const parentLabel = parent
    ? referenceLabels?.[parent.id] ?? listView.workObjects.find((candidate) => candidate.id === parent.id)?.title ?? parent.id
    : null;

  // Keep the latest editable state reachable from the debounced save without stale closures.
  const latest = useRef({ applicableFieldDefinitions, drafts: fieldValueDrafts, itemId: item?.id ?? null, title, typeId });
  latest.current = { applicableFieldDefinitions, drafts: fieldValueDrafts, itemId: item?.id ?? null, title, typeId };
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    if (!item) return;
    setTitle(item.title);
    setTypeId(item.type?.id ?? "");
    setError(null);
    setSaveStatus("idle");
  }, [item]);

  useEffect(() => {
    setReplyTo(null);
  }, [item?.id]);

  useEffect(() => {
    setFieldValueDrafts(createFieldValueDrafts(applicableFieldDefinitions, fieldValues));
  }, [applicableFieldDefinitions, fieldValues, item?.id]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  async function flushSave() {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const snapshot = latest.current;
    if (!snapshot.itemId || snapshot.title.trim().length < 2 || !snapshot.typeId) return;
    setSaveStatus("saving");
    setError(null);
    try {
      await onSaveRef.current(
        { id: snapshot.itemId, title: snapshot.title.trim(), typeId: snapshot.typeId },
        buildFieldValueInput(snapshot.applicableFieldDefinitions, snapshot.drafts),
      );
      setSaveStatus("saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save item.");
      setSaveStatus("error");
    }
  }

  function scheduleSave() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus("saving");
    saveTimer.current = setTimeout(() => void flushSave(), 700);
  }

  function handleOpenChange(nextOpen: boolean, eventDetails?: { event?: Event }) {
    // The dialog's Escape handler runs in the capture phase — before the
    // document editor can claim the key — so Escapes that belong to the
    // editor (deselect a block, close the "/" palette, cancel a link edit)
    // are re-derived here and must not dismiss the dialog.
    if (!nextOpen && isEditorConsumedEscape(eventDetails?.event)) return;
    if (!nextOpen && saveTimer.current) void flushSave();
    onOpenChange(nextOpen);
  }

  function commitTitle() {
    if (!item) return;
    if (title.trim().length < 2) {
      setTitle(item.title);
      return;
    }
    if (title.trim() !== item.title) void flushSave();
  }

  async function archive() {
    if (!item) return;
    setError(null);
    try {
      await onArchive(item.id);
      setIsArchiveOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive item.");
    }
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="flex h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] flex-col gap-0 overflow-hidden p-0 sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:w-[calc(100%-2rem)] sm:max-w-6xl">
        {item ? (
          <>
            <DialogTitle className="sr-only">{item.title}</DialogTitle>
            <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="flex min-h-0 flex-col overflow-hidden">
                <div className="flex items-center gap-2 border-b border-border/70 px-6 py-2.5 pr-14">
                  <Select
                    onValueChange={(nextValue) => {
                      if (nextValue) {
                        setTypeId(nextValue);
                        scheduleSave();
                      }
                    }}
                    value={typeId}
                  >
                    <SelectTrigger aria-label={t("item.type")} className="h-7 w-auto gap-1.5 rounded-full px-2.5 text-xs font-medium">
                      {selectedType?.name ?? "Type"}
                    </SelectTrigger>
                    <SelectContent align="start">
                      <SelectGroup>
                        {listView.types.map((type) => (
                          <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {statusControl}
                  <div className="ml-auto flex items-center gap-2">
                    <SaveIndicator status={saveStatus} />
                    <DropdownMenu>
                      <DropdownMenuTrigger render={<Button aria-label={t("item.moreActions")} size="icon-sm" variant="ghost"><MoreHorizontal /></Button>} />
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={() => setIsArchiveOpen(true)} variant="destructive">{t("item.archive")}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* pl-14 leaves a gutter for the editor's block handle ("+" and
                    grip render to the left of the hovered line and would be
                    clipped by this scroll container otherwise). */}
                <div className="min-h-0 flex-1 overflow-y-auto py-5 pl-14 pr-6">
                  {parent ? (
                    <button
                      className="mb-2 flex min-w-0 max-w-full items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                      onClick={() => onOpenRelated?.(parent.id)}
                      type="button"
                    >
                      <CornerLeftUp className="size-3 shrink-0" />
                      <span className="shrink-0">{parent.fieldName}</span>
                      <span className="min-w-0 truncate font-medium text-foreground/80">{parentLabel}</span>
                    </button>
                  ) : null}
                  <textarea
                    aria-label={t("item.title")}
                    className="field-sizing-content w-full resize-none border-0 bg-transparent p-0 text-2xl font-semibold leading-tight outline-none placeholder:text-muted-foreground/40"
                    onBlur={commitTitle}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder={t("item.untitled")}
                    rows={1}
                    value={title}
                  />

                  <div className="mt-5">
                    <WorkObjectPropertyList
                      currentWorkObjectId={item.id}
                      fieldDefinitions={valueFieldDefinitions}
                      fieldValueDrafts={fieldValueDrafts}
                      listView={listView}
                      onFieldValueChange={(fieldId, value) => {
                        setFieldValueDrafts((current) => ({ ...current, [fieldId]: value }));
                        scheduleSave();
                      }}
                      onHideForType={onHideFieldForType ? (field) => void onHideFieldForType(field, typeId) : undefined}
                      referenceLabels={referenceLabels}
                      rememberKey={typeId || null}
                    />
                  </div>

                  {/* The other side of the item's typed relations: nothing
                      points here through a field on this item, so it would be
                      invisible without asking from this end. */}
                  {inboundRelations.data.length > 0 ? (
                    <div className="mt-5 flex flex-col gap-3">
                      {inboundRelations.data.map((group) => (
                        <div className="flex flex-col gap-1" key={group.fieldDefinitionId}>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xs font-medium text-muted-foreground">{group.name}</span>
                            {/* Сколько из состава ещё открыто. У релиза это ответ
                                на «можно ли выкатывать», у эпика — «сколько
                                осталось»: один и тот же вопрос к любой ветке. */}
                            <span className="text-xs text-muted-foreground/70">
                              {t("item.relationProgress", {
                                done: group.items.filter((related) => related.statusCategory === "closed" || related.statusCategory === "done").length,
                                total: group.items.length,
                              })}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            {group.items.map((related) => (
                              <button
                                className="flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm transition-colors hover:bg-muted/50"
                                key={related.id}
                                onClick={() => onOpenRelated?.(related.id)}
                                type="button"
                              >
                                {related.itemKey ? (
                                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground/60">{related.itemKey}</span>
                                ) : null}
                                <span className="min-w-0 truncate">{related.title}</span>
                                {related.typeName ? (
                                  <span className="ml-auto shrink-0 text-xs text-muted-foreground/70">{related.typeName}</span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* The item's rich description: visually part of the task,
                      technically its always-present attached document. */}
                  <div className="mt-6">
                    {workObjectDocument.data ? (
                      <DocumentEditor
                        document={workObjectDocument.data}
                        key={workObjectDocument.data.id}
                        onReload={workObjectDocument.reload}
                        persist={workObjectDocument.persist}
                        placeholder={t("item.addDescription")}
                      />
                    ) : null}
                  </div>

                  {error ? <Alert className="mt-4" variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
                </div>
              </div>

              <aside className="hidden min-h-0 flex-col border-l border-border/70 bg-muted/10 lg:flex">
                <div className="border-b border-border/70 px-4 py-3 pr-14">
                  <h3 className="text-sm font-semibold">{t("item.activity")}</h3>
                </div>
                <WorkObjectActivity
                  auditEvents={auditEvents}
                  comments={comments.data}
                  currentUserId={currentUserId}
                  isLoading={isLoadingAudit || comments.isLoading}
                  members={mentionMembers}
                  onDeleteComment={comments.deleteComment}
                  onReply={setReplyTo}
                  onToggleReaction={async (commentId, emoji) => {
                    await comments.toggleReaction(commentId, emoji);
                  }}
                  onUpdateComment={async (input) => {
                    await comments.updateComment(input);
                  }}
                  workObjectId={item.id}
                />
                <div className="px-3 pb-3 pt-1.5">
                  <CommentComposer
                    key={item.id}
                    members={mentionMembers}
                    onCancelReply={() => setReplyTo(null)}
                    onSubmit={async ({ attachmentIds, body }) => {
                      await comments.createComment({ attachmentIds, body, replyToCommentId: replyTo?.id ?? null });
                      setReplyTo(null);
                    }}
                    replyTo={
                      replyTo
                        ? {
                            author: replyTo.author.displayName ?? replyTo.author.email ?? "Unknown",
                            snippet: replyTo.bodyText || "Attachment",
                          }
                        : null
                    }
                    workObjectId={item.id}
                  />
                </div>
              </aside>
            </div>
          </>
        ) : null}
      </DialogContent>

      <AlertDialog onOpenChange={setIsArchiveOpen} open={isArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("item.archiveConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("itemArchive.description")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isArchiving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={isArchiving} onClick={archive} variant="destructive">
              {isArchiving ? t("itemArchive.archiving") : t("item.archive")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// Куда карточка входит по иерархии. Связь-иерархию schema помечает сама
// (`relation.isHierarchy`), поэтому крошка работает и там, где родителя зовут
// иначе, — «Эпик», «Родительская задача», «Parent item».
function hierarchyParent(
  fields: WorkObjectFieldDefinition[],
  drafts: Record<string, WorkObjectFieldValueDraft>,
): { fieldName: string; id: string } | null {
  for (const field of fields) {
    const relation = (field.config as { relation?: { isHierarchy?: boolean } } | undefined)?.relation;
    if (!relation?.isHierarchy || field.type !== "work_object_relation") continue;
    const draft = drafts[field.id];
    const id = Array.isArray(draft) ? draft[0] : draft;
    if (typeof id === "string" && id) return { fieldName: field.name, id };
  }
  return null;
}

function isEditorConsumedEscape(event: Event | undefined): boolean {
  if (!(event instanceof KeyboardEvent) || event.key !== "Escape") return false;
  // The "/" palette is open — Escape closes it, not the dialog.
  if (document.querySelector('[role="listbox"][aria-label={t("editor.insertBlock")}]')) return true;
  const target = event.target instanceof Element ? event.target : null;
  // Editor chrome (link editors, image toolbar/handles, table handles)
  // handles its own Escape.
  if (target?.closest("[data-editor-chrome], [data-table-handles]")) return true;
  // A selected block (image, divider, table) — Escape deselects it.
  const editorRoot = target?.closest(".document-editor");
  return Boolean(editorRoot?.querySelector(".ProseMirror-selectednode"));
}

function SaveIndicator({ status }: { status: SaveStatus }) {
  const t = useT();
  if (status === "saving") {
    return <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="size-3.5 animate-spin" /> {t("item.saving")}</span>;
  }
  if (status === "saved") {
    return <span className="text-xs text-muted-foreground">{t("item.saved")}</span>;
  }
  if (status === "error") {
    return <span className="text-xs text-destructive">{t("item.saveFailed")}</span>;
  }
  return null;
}

